import { useEffect, useState, useTransition } from 'react';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AiOutlinePlus } from 'react-icons/ai';
import { IoMdClose } from 'react-icons/io';

import { ConfirmationModal } from '../FeedBack/ConfirmationModal';
import ButtonHeadless from '../General/ButtonHeadless';
import { Language, Languages } from '@wajeez-web/core/utils';
import { Spin } from '../..';

interface LanguagesLayoutProps {
  allowedLanguages?: Languages[];

  shouldUpdateSelectedLanguage?: (
    oldLanguage: Languages | undefined,
    newLanguage: Languages
  ) => boolean;
  onSelectedLanguageChanged?: (
    oldLanguage: Languages | undefined,
    newLanguage: Languages
  ) => void;
  defaultSelectedLanguage?: Languages;
  defaultLanguages?: Languages[];

  renderPage: (language: Languages) => JSX.Element;
  shouldAdd?: (language: Languages) => boolean;
  onAdd?: (language: Languages) => void;
  shouldDelete?: (language: Languages) => boolean;
  onDelete?: (language: Languages) => void;
}

const supportedLanguages = [
  {
    value: Languages.AR,
    label: 'Arabic',
    activeColor: '#1f798320',
    inactiveColor: '#1f7983',
  },
  {
    value: Languages.EN,
    label: 'English',
    activeColor: '#64008020',
    inactiveColor: '#640080',
  },
];

export const LanguagesLayout = ({
  allowedLanguages,
  renderPage,
  onAdd,
  onDelete,
  defaultSelectedLanguage = Languages.AR,
  shouldAdd,
  shouldDelete,
  shouldUpdateSelectedLanguage,
  onSelectedLanguageChanged,
  defaultLanguages = [Languages.AR],
}: LanguagesLayoutProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<Languages>(
    defaultSelectedLanguage
  );
  const [languages, setLanguages] = useState<Languages[]>(defaultLanguages);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState({
    isOpen: false,
    language: Languages.AR,
  });
  const [isTabChanging, setIsTabChanging] = useState(false);

  const isEnglishAdded =
    languages.find((l) => l === Languages.EN) !== undefined;
  const isArabicAdded = languages.find((l) => l === Languages.AR) !== undefined;
  const isLanguageRemoveIconShown = languages.length > 1;

  const dir = useCommonSelector(({ common }) => common.settings.dir);

  const selectedLanguageObject = supportedLanguages.find(
    (l) => l.value === selectedLanguage
  );

  const [isPending, startTransition] = useTransition();

  const renderAddedLanguages = () => {
    return languages.map((l) => {
      const language = supportedLanguages.find((ll) => ll.value === l);

      const isSelected = selectedLanguage === l;

      return (
        <div key={l}>
          <div
            className={`py-3 px-6 flex justify-center items-center ${
              isSelected ? 'text-black' : 'text-white'
            } font-bold cursor-pointer`}
            style={{
              backgroundColor: isSelected
                ? language?.activeColor
                : language?.inactiveColor,
            }}
            onClick={() => {
              if (!isSelected) {
                setIsTabChanging(true);

                setTimeout(() => {
                  setIsTabChanging(false);
                }, 300);

                if (
                  shouldUpdateSelectedLanguage == undefined ||
                  shouldUpdateSelectedLanguage(selectedLanguage, l)
                ) {
                  onSelectedLanguageChanged &&
                    onSelectedLanguageChanged(selectedLanguage, l);
                  setSelectedLanguage(l);
                }
              }
            }}
          >
            {isLanguageRemoveIconShown && dir === 'ltr' && (
              <div
                className="ml-2 cursor-pointer"
                onClick={() => {
                  setIsConfirmationModalOpen({
                    isOpen: true,
                    language: l,
                  });
                }}
              >
                <IoMdClose />
              </div>
            )}
            <ReturnTranslatedText
              text={`inputs.labels.${language?.label}`}
              origintext={language?.label}
            />
            {isLanguageRemoveIconShown && dir === 'rtl' && (
              <div
                className="mr-2 cursor-pointer"
                onClick={() => {
                  setIsConfirmationModalOpen({
                    isOpen: true,
                    language: l,
                  });
                }}
              >
                <IoMdClose />
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <div className="mt-4">
        <div className="w-full flex justify-start items-center">
          <div className="flex justify-start items-center overflow-hidden">
            {renderAddedLanguages()}
          </div>
          {!isEnglishAdded &&
            (allowedLanguages == undefined ||
              allowedLanguages.length == 0 ||
              allowedLanguages.includes(Languages.EN)) && (
              <AddLanguageButton
                onClick={() => {
                  startTransition(() => {
                    if (shouldAdd == undefined || shouldAdd(Languages.EN)) {
                      onAdd && onAdd(Languages.EN);
                      setLanguages((ls) => [...ls, Languages.EN]);
                      setSelectedLanguage(Languages.EN);
                    }
                  });
                }}
                label="English"
              />
            )}
          {!isArabicAdded &&
            (allowedLanguages == undefined ||
              allowedLanguages.length == 0 ||
              allowedLanguages.includes(Languages.AR)) && (
              <AddLanguageButton
                onClick={() => {
                  startTransition(() => {
                    if (shouldAdd == undefined || shouldAdd(Languages.AR)) {
                      onAdd && onAdd(Languages.AR);
                      setLanguages((ls) => [...ls, Languages.AR]);
                      setSelectedLanguage(Languages.AR);
                    }
                  });
                }}
                label="Arabic"
              />
            )}
          {isConfirmationModalOpen.isOpen && (
            <ConfirmationModal
              isOpen={isConfirmationModalOpen.isOpen}
              onClose={() => {
                setIsConfirmationModalOpen({
                  isOpen: false,
                  language: isConfirmationModalOpen.language,
                });
              }}
              message="The entered data will be lost, are you sure that you want to remove this language?"
              onSubmit={() => {
                startTransition(() => {
                  if (
                    shouldDelete == undefined ||
                    shouldDelete(isConfirmationModalOpen.language)
                  ) {
                    onDelete && onDelete(isConfirmationModalOpen.language);
                    setSelectedLanguage(
                      languages.filter(
                        (s) => s !== isConfirmationModalOpen.language
                      )[0]
                    );
                    setLanguages((ls) =>
                      ls.filter((s) => s !== isConfirmationModalOpen.language)
                    );
                  }

                  setIsConfirmationModalOpen({
                    isOpen: false,
                    language: isConfirmationModalOpen.language,
                  });
                });
              }}
            />
          )}
        </div>
      </div>
      <div
        className={`w-full pt-3 pb-6 ps-4 pe-4`}
        style={{
          backgroundColor: selectedLanguageObject?.activeColor,
          color: selectedLanguageObject?.inactiveColor,
        }}
      >
        {!isTabChanging ? (
          renderPage(selectedLanguage)
        ) : (
          <div className="w-full h-[150px] flex justify-center items-center">
            <Spin colorStyle="text-currentcolor" />
          </div>
        )}
      </div>
    </>
  );
};

interface AddLanguageButtonProps {
  onClick: () => void;
  label: string;
}

export const AddLanguageButton = ({
  onClick,
  label,
}: AddLanguageButtonProps) => {
  const dir = useCommonSelector(({ common }) => common.settings.dir);

  return (
    <div className="ms-4 w-[130px]">
      <ButtonHeadless
        bgStyle="bg-transparent"
        paddingYStyle="py-2"
        borderRadiusStyle="rounded-none"
        action={onClick}
      >
        <div className="text-primary flex justify-center items-center">
          {dir === 'ltr' && (
            <div className="ml-2">
              <AiOutlinePlus />
            </div>
          )}
          <ReturnTranslatedText
            text={`buttons.${'Add ' + label}`}
            origintext={'Add ' + label}
          />
          {dir === 'rtl' && (
            <div className="mr-2">
              <AiOutlinePlus />
            </div>
          )}
        </div>
      </ButtonHeadless>
    </div>
  );
};
